import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import * as Sentry from '@sentry/react';

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
import { ROUTES } from '@/config/routes';
import { NirpLayout } from '@/components/nirp/NirpLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { useCmsPermissions } from '@/modules/cms/hooks/useCmsPermissions';
import { useCurrentPlan } from '@/modules/cms/hooks/useCms';
import { useAuthStore } from '@/store/authStore';

import { useThemeStore } from '@/store/themeStore';
import { useEffect } from 'react';
import { ThemePlugin } from '@/components/ThemePlugin';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// Auth — direct imports (not lazy) so Router context is available immediately
// Login is handled by Keycloak's hosted page; no in-app /login route.
import { Register } from '@/pages/Register';
import { AcceptInvite } from '@/pages/AcceptInvite';
import OrganizationRegistrationPage from '@/pages/OrganizationRegistration';
import WaitingForApprovalPage from '@/pages/WaitingForApproval';
import RejectedOrganizationPage from '@/pages/RejectedOrganization';
import NoOrganizationPage from '@/pages/NoOrganization';
import { PageLoader } from '@/components/ui/scan-bar';

// ── Public marketing landing page
const LandingPage        = lazy(() => import('@/pages/LandingPage'));

// ── Shared Home Dashboard
const HomeDashboard      = lazy(() => import('@/pages/HomeDashboard'));
const RolesPage          = lazy(() => import('@/pages/admin/RolesPage'));
const UnauthorizedPage   = lazy(() => import('@/pages/UnauthorizedPage'));
const NotFoundPage       = lazy(() => import('@/pages/NotFoundPage'));
const DirectoryPage      = lazy(() => import('@/pages/Directory'));

// ── Asset Management pages (each wraps itself in MainLayout) ────
const DashboardPage      = lazy(() => import('@/pages/Dashboard'));
const AssetsPage         = lazy(() => import('@/pages/Assets'));
const VulnerabilitiesPage = lazy(() => import('@/pages/Vulnerabilities'));
const AssetDetail        = lazy(() => import('@/pages/AssetDetail'));
const CreateAsset        = lazy(() => import('@/pages/CreateAsset'));
const EditAsset          = lazy(() => import('@/pages/EditAsset'));
const TeamPage           = lazy(() => import('@/pages/TeamPage'));
const AgentsPage         = lazy(() => import('@/pages/AgentsPage'));
const OrganizationsPage  = lazy(() => import('@/pages/admin/OrganizationsPage'));
const OrganizationDetailPage = lazy(() => import('@/pages/admin/OrganizationDetailPage'));
const UsersPage          = lazy(() => import('@/pages/admin/UsersPage'));
const UserDetailPage     = lazy(() => import('@/pages/admin/UserDetailPage'));
const SupportAdmin       = lazy(() => import('@/pages/admin/SupportAdmin'));
const AuditLogPage       = lazy(() => import('@/pages/AuditLogPage'));
const ExternalApiKeysPage = lazy(() => import('@/pages/admin/ExternalApiKeysPage'));
const ProfilePage        = lazy(() => import('@/pages/ProfilePage'));

// ── NIRP pages (wrapped by NirpLayout below) ────────────────────
const OrgDashboard          = lazy(() => import('@/pages/nirp/org/OrgDashboard'));
const SubmitIncidentPage    = lazy(() => import('@/pages/nirp/org/SubmitIncidentPage'));
const MyIncidentsPage       = lazy(() => import('@/pages/nirp/org/MyIncidentsPage'));
const OrgThreatIntelDashboard = lazy(() => import('@/pages/nirp/org/OrgThreatIntelDashboard'));
const OrgThreatFeedPage     = lazy(() => import('@/pages/nirp/org/OrgThreatFeedPage'));
const OrgProfilePage        = lazy(() => import('@/pages/nirp/org/OrgProfilePage'));
const UploadCsvPage         = lazy(() => import('@/pages/nirp/org/UploadCsvPage'));
const NotificationsPage     = lazy(() => import('@/pages/nirp/NotificationsPage'));
const AdminDashboard        = lazy(() => import('@/pages/nirp/admin/AdminDashboard'));
const AdminIncidentsPage    = lazy(() => import('@/pages/nirp/admin/AdminIncidentsPage'));
const ThreatIntelPage       = lazy(() => import('@/pages/nirp/admin/ThreatIntelPage'));
const AuthoredThreatQueuePage  = lazy(() => import('@/pages/nirp/admin/AuthoredThreatQueuePage'));
const AuthoredThreatCreatePage = lazy(() => import('@/pages/nirp/admin/AuthoredThreatCreatePage'));
const SupportPage           = lazy(() => import('@/pages/SupportPage'));
const AnalyticsPage         = lazy(() => import('@/pages/AnalyticsPage'));
// Dashboard Builder merged into Analytics; /dashboards now redirects there.
const DashboardBuilderRedirect = lazy(() => import('@/pages/DashboardBuilderRedirect'));
const SentryTestPage        = lazy(() => import('@/pages/SentryTest'));


// ── CMS — Case Management System ────────────────────────────────
const CmsDashboardPage   = lazy(() => import('@/modules/cms/pages/DashboardPage'));
const CmsCasesPage       = lazy(() => import('@/modules/cms/pages/CasesPage'));
const CmsCaseBoardPage   = lazy(() => import('@/modules/cms/pages/CaseBoardPage'));
const CmsCaseDetailPage  = lazy(() => import('@/modules/cms/pages/CaseDetailsPage'));
const CmsAlertsPage      = lazy(() => import('@/modules/cms/pages/AlertsPage'));
const CmsAlertDetailPage = lazy(() => import('@/modules/cms/pages/AlertDetailsPage'));
const CmsAlertSourcesPage = lazy(() => import('@/modules/cms/pages/AlertSourcesPage'));
const CmsTeamPage        = lazy(() => import('@/modules/cms/pages/CmsTeamPage'));
const CmsSettingsPage    = lazy(() => import('@/modules/cms/pages/CmsSettingsPage'));
const CmsCustomersPage   = lazy(() => import('@/modules/cms/pages/CmsCustomersPage'));

/** Customer register and on-premise licensing — platform_admin only. */
function CmsCustomersGuard() {
  const { hasRole } = useAuthStore();
  return hasRole('platform_admin') ? <CmsCustomersPage /> : <Navigate to={ROUTES.CMS_DASHBOARD} replace />;
}
const CmsMetricsPage     = lazy(() => import('@/modules/cms/pages/CmsMetricsPage'));
const CertCollaborationPage = lazy(() => import('@/modules/cms/pages/CertCollaborationPage'));

// ── CERT Cooperation Portal (public — no Keycloak) ──────────────
const CertPortalPage     = lazy(() => import('@/modules/cms/pages/CertPortalPage'));

/** Redirects to CMS dashboard if the user's role is not authorized. */
function CmsRoleGuard({ allowed, children }: { allowed: boolean; children: React.ReactNode }) {
  return allowed ? <>{children}</> : <Navigate to={ROUTES.CMS_DASHBOARD} replace />;
}

/** Case Board — manager, org_admin, platform_admin only */
function CmsCaseBoardGuard() {
  const p = useCmsPermissions();
  return <CmsRoleGuard allowed={p.canViewCaseBoard}><CmsCaseBoardPage /></CmsRoleGuard>;
}

/** App Integrations — allowed for all roles with canViewAlertSources */
function CmsAlertSourcesGuard() {
  const p = useCmsPermissions();
  return <CmsRoleGuard allowed={p.canViewAlertSources}><CmsAlertSourcesPage /></CmsRoleGuard>;
}

/** Team Management — manager, org_admin and platform_admin */
function CmsTeamGuard() {
  const p = useCmsPermissions();
  useEffect(() => {
    void useAuthStore.getState().refreshSession(30);
  }, []);
  return <CmsRoleGuard allowed={p.canViewTeamManagement}><CmsTeamPage /></CmsRoleGuard>;
}

/**
 * CmsTrialGuard — protects CMS routes:
 * 1. For platform_admin: allows ONLY /cms/settings and /cms/audit (redirects forbidden CMS paths to /cms/settings).
 * 2. For tenant users: blocks CMS routes (except /cms/settings) when tenant free trial is not activated.
 */
function CmsTrialGuard() {
  const location = useLocation();
  const { hasRole } = useAuthStore();
  const isPlatformAdmin = hasRole('platform_admin');

  // Platform admin is exempt from tenant trial restrictions
  if (isPlatformAdmin) {
    return <Outlet />;
  }

  const { plan: currentPlan, loading } = useCurrentPlan();

  if (loading) {
    return <PageLoader label="Loading Case Management System..." />;
  }

  const trialNotActivated =
    currentPlan?.plan?.name === 'free_trial' &&
    currentPlan?.trial?.trialActivated === false;

  // If trial not activated, only allow /cms/settings
  if (trialNotActivated && location.pathname !== ROUTES.CMS_SETTINGS) {
    return <Navigate to={ROUTES.CMS_SETTINGS} replace />;
  }

  return <Outlet />;
}

function CmsIndexRedirect() {
  return <Navigate to={ROUTES.CMS_DASHBOARD} replace />;
}

/** CERT Collaboration Management — manager, org_admin */
function CmsCertCollaborationGuard() {
  const p = useCmsPermissions();
  return <CmsRoleGuard allowed={p.canReviewCertRequests}><CertCollaborationPage /></CmsRoleGuard>;
}

// ── Platform Admin — Sold Services / Revenue ────────────────────
const ServicesOverviewPage = lazy(() => import('@/modules/services-admin/pages/ServicesOverviewPage'));
const SoldServicesPage     = lazy(() => import('@/modules/services-admin/pages/SoldServicesPage'));

/**
 * Old notification links (/nirp/incidents/:id, /support/tickets/:id) are
 * still stored on existing rows and pointed at routes that never existed.
 * Send them to the list page that opens the record, picking the admin or org
 * page by role the same way lib/notificationTarget.ts does.
 */
function LegacyIncidentRedirect() {
  const { id = '' } = useParams();
  const isPlatformAdmin = useAuthStore((s) => s.hasRole('platform_admin'));
  const base = isPlatformAdmin ? ROUTES.NIRP_ADMIN_INCIDENTS : ROUTES.NIRP_MY_INCIDENTS;
  return <Navigate to={`${base}?id=${encodeURIComponent(id)}`} replace />;
}

function LegacyTicketRedirect() {
  const { id = '' } = useParams();
  const isPlatformAdmin = useAuthStore((s) => s.hasRole('platform_admin'));
  const base = isPlatformAdmin ? ROUTES.SUPPORT_ADMIN : ROUTES.SUPPORT;
  return <Navigate to={`${base}?ticket=${encodeURIComponent(id)}`} replace />;
}

function Loader() {
  return <PageLoader label="Loading BlueB" />;
}

export default function App() {
  const { theme, colorTheme } = useThemeStore();

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark', 'brand');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  useEffect(() => {
    window.document.documentElement.setAttribute('data-color', colorTheme);
  }, [colorTheme]);

  // Handle system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(mediaQuery.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <BrowserRouter>
      <Suspense fallback={<Loader />}>
        <SentryRoutes>
          {/* ── Public marketing landing (also handles "/") ── */}
          <Route path={ROUTES.LANDING} element={<LandingPage />} />

          {/* ── Auth ── */}
          {/* Login: handled by Keycloak's hosted page (no in-app route).
              Register/RequestAccess: stub that forwards to Keycloak's
              registration page; preserved so old marketing links keep working. */}
          <Route path={ROUTES.REGISTER} element={<Register />} />
          <Route path={ROUTES.UNAUTHORIZED} element={<UnauthorizedPage />} />
          <Route path={ROUTES.ACCEPT_INVITE} element={<AcceptInvite />} />

          {/* ── CERT Cooperation Portal (public — no Keycloak login required) ── */}
          <Route path={ROUTES.CERT_PORTAL} element={<CertPortalPage />} />

          {/* ── Protected Routes ── */}
          <Route element={<ProtectedRoute />}>
            <Route element={<OnboardingGuard><Outlet /></OnboardingGuard>}>
              <Route path={ROUTES.NO_ORGANIZATION} element={<NoOrganizationPage />} />
              <Route path={ROUTES.ORG_REGISTRATION} element={<OrganizationRegistrationPage />} />
              <Route path={ROUTES.WAITING_APPROVAL} element={<WaitingForApprovalPage />} />
              <Route path={ROUTES.REJECTED_ORGANIZATION} element={<RejectedOrganizationPage />} />

              {/* Home Dashboard */}
              <Route path={ROUTES.HOME} element={<HomeDashboard />} />
              <Route path={ROUTES.DIRECTORY} element={<DirectoryPage />} />

              {/* Asset Management */}
              <Route path={ROUTES.DASHBOARD}           element={<DashboardPage />} />
              <Route
                path={ROUTES.ASSETS}
                element={
                  <ProtectedRoute requiredPermission="assets:read">
                    <AssetsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.VULNERABILITIES}
                element={
                  <ProtectedRoute requiredPermission="vulnerabilities:read">
                    <VulnerabilitiesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CREATE_ASSET}
                element={
                  <ProtectedRoute requiredPermission="assets:create">
                    <CreateAsset />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.ASSET_DETAIL}
                element={
                  <ProtectedRoute requiredPermission="assets:read">
                    <AssetDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.EDIT_ASSET}
                element={
                  <ProtectedRoute requiredPermission="assets:update">
                    <EditAsset />
                  </ProtectedRoute>
                }
              />
              <Route path={ROUTES.TEAM}                element={<TeamPage />} />
              <Route
                path={ROUTES.ENROLL_ASSET}
                element={
                  <ProtectedRoute requiredPermission="assets:enroll-secret">
                    <AgentsPage />
                  </ProtectedRoute>
                }
              />
              <Route path={ROUTES.ADMIN_ORGANIZATIONS} element={<OrganizationsPage />} />
              <Route path={ROUTES.ADMIN_ORGANIZATION_DETAIL} element={<OrganizationDetailPage />} />
              {/* Pending organizations now live on the Organizations page under
                  the Pending status filter; keep the old path working. */}
              <Route
                path={ROUTES.ADMIN_PENDING}
                element={<Navigate to={`${ROUTES.ADMIN_ORGANIZATIONS}?status=pending`} replace />}
              />
              <Route path={ROUTES.ADMIN_USERS}         element={<UsersPage />} />
              <Route
                path={ROUTES.ADMIN_ROLES}
                element={
                  <ProtectedRoute requiredPermission="roles:read">
                    <RolesPage />
                  </ProtectedRoute>
                }
              />
              <Route path={ROUTES.ADMIN_USER_DETAIL}   element={<UserDetailPage />} />
              <Route path={ROUTES.SUPPORT_ADMIN}       element={<SupportAdmin />} />
              <Route path={ROUTES.ADMIN_AUDIT_LOGS}    element={<AuditLogPage />} />
              <Route path={ROUTES.ADMIN_EXTERNAL_API_KEYS} element={<ExternalApiKeysPage />} />
              <Route path={ROUTES.ADMIN_SERVICES} element={<ServicesOverviewPage />} />
              <Route path={ROUTES.ADMIN_SERVICES_SOLD} element={<SoldServicesPage />} />
              {/* Legacy routes -> namespaced routes */}
              <Route path="/home" element={<Navigate to={ROUTES.HOME} replace />} />
              <Route path="/nirp/home" element={<Navigate to={ROUTES.HOME} replace />} />
              <Route path="/dashboard" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
              <Route path="/agents" element={<Navigate to={ROUTES.ENROLL_ASSET} replace />} />
              <Route path="/incidents" element={<Navigate to={ROUTES.NIRP_MY_INCIDENTS} replace />} />
              <Route path="/submit-incident" element={<Navigate to={ROUTES.NIRP_SUBMIT_INCIDENT} replace />} />
              <Route path="/threat-feed" element={<Navigate to={ROUTES.NIRP_THREAT_FEED} replace />} />
              <Route path="/messages" element={<Navigate to={ROUTES.NIRP_MESSAGES} replace />} />
              <Route path="/upload" element={<Navigate to={ROUTES.NIRP_UPLOAD} replace />} />
              <Route path="/admin/incidents" element={<Navigate to={ROUTES.NIRP_ADMIN_INCIDENTS} replace />} />
              <Route path="/admin/threat-intel" element={<Navigate to={ROUTES.NIRP_ADMIN_THREAT_INTEL} replace />} />
              <Route path="/nirp/admin/pending-organizations" element={<Navigate to={ROUTES.ADMIN_PENDING} replace />} />
              <Route path="/nirp/incidents/:id" element={<LegacyIncidentRedirect />} />
              <Route path="/support/tickets/:id" element={<LegacyTicketRedirect />} />
              <Route path={ROUTES.USER_PROFILE}        element={<ProfilePage />} />

              {/* NIRP — wrapped by NirpLayout */}
              <Route element={<NirpLayout />}>
                <Route
                  path={ROUTES.NIRP_ORG_DASHBOARD}
                  element={
                    <ProtectedRoute requiredPermission="incidents:read">
                      <OrgDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_SUBMIT_INCIDENT}
                  element={
                    <ProtectedRoute requiredPermission="incidents:create">
                      <SubmitIncidentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_MY_INCIDENTS}
                  element={
                    <ProtectedRoute requiredPermission="incidents:read">
                      <MyIncidentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_THREAT_DASHBOARD}
                  element={
                    <ProtectedRoute requiredPermission="threat-intel:read">
                      <OrgThreatIntelDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_THREAT_FEED}
                  element={
                    <ProtectedRoute requiredPermission="threat-intel:read">
                      <OrgThreatFeedPage />
                    </ProtectedRoute>
                  }
                />
                <Route path={ROUTES.NIRP_MESSAGES}         element={<NotificationsPage />} />
                <Route
                  path={ROUTES.NIRP_UPLOAD}
                  element={
                    <ProtectedRoute requiredPermission="incidents:create">
                      <UploadCsvPage />
                    </ProtectedRoute>
                  }
                />
                <Route path={ROUTES.NIRP_PROFILE}          element={<OrgProfilePage />} />
                {/* Admin */}
                <Route
                  path={ROUTES.NIRP_ADMIN_DASHBOARD}
                  element={
                    <ProtectedRoute requiredPermission="incidents:read">
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_ADMIN_INCIDENTS}
                  element={
                    <ProtectedRoute requiredPermission="incidents:read">
                      <AdminIncidentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_ADMIN_THREAT_INTEL}
                  element={
                    <ProtectedRoute requiredPermission="threat-intel:read">
                      <ThreatIntelPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_ADMIN_THREAT_SHARE_QUEUE}
                  element={
                    <ProtectedRoute requiredPermission="authored-threat-shares:read">
                      <AuthoredThreatQueuePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.NIRP_ADMIN_THREAT_SHARE_CREATE}
                  element={
                    <ProtectedRoute requiredPermission="authored-threat-shares:create">
                      <AuthoredThreatCreatePage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              
              <Route path={ROUTES.SUPPORT} element={<SupportPage />} />
              {/* Gated on the permission the analytics service itself enforces,
                  so the route can't render a page whose every request 403s. */}
              <Route
                path={ROUTES.ANALYTICS}
                element={
                  <ProtectedRoute requiredPermission="analytics:read">
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              {/* Legacy /dashboards → merged into /analytics */}
              <Route path={ROUTES.DASHBOARD_BUILDER} element={<DashboardBuilderRedirect />} />
              <Route path={ROUTES.SENTRY_TEST} element={<SentryTestPage />} />

              {/* CMS — Case Management System */}
              <Route element={<CmsTrialGuard />}>
                <Route path={ROUTES.CMS} element={<CmsIndexRedirect />} />
                <Route path={ROUTES.CMS_DASHBOARD} element={<CmsDashboardPage />} />
                <Route path={ROUTES.CMS_CASES}     element={<CmsCasesPage />} />
                <Route path={ROUTES.CMS_CASE_BOARD} element={<CmsCaseBoardGuard />} />
                <Route path={ROUTES.CMS_CASE_DETAIL} element={<ErrorBoundary><CmsCaseDetailPage /></ErrorBoundary>} />
                <Route path={ROUTES.CMS_ALERTS}    element={<CmsAlertsPage />} />
                <Route path={ROUTES.CMS_ALERT_DETAIL} element={<ErrorBoundary><CmsAlertDetailPage /></ErrorBoundary>} />
                <Route path={ROUTES.CMS_REPORTS}   element={<Navigate to="/cms/dashboard?tab=analytics" replace />} />
                <Route path={ROUTES.CMS_ALERT_SOURCES} element={<CmsAlertSourcesGuard />} />
                <Route path={ROUTES.CMS_TEAM}      element={<CmsTeamGuard />} />
                {/* Embedded mode shares the platform's own notification feed
                    (same backend, no data lost — see NotificationsPage.tsx's
                    CMS type support) rather than CMS's own page, which exists
                    only because the standalone/on-prem bundle has no other
                    notification UI at all. */}
                <Route path={ROUTES.CMS_NOTIFICATIONS} element={<Navigate to={ROUTES.NIRP_MESSAGES} replace />} />
                {/* Embedded mode shares the platform's own audit log page
                    (same audit-service backend, no data lost — see
                    AuditLogPage.tsx's 'case-management' service filter)
                    rather than CMS's own page, which exists only because
                    standalone/on-prem writes to and reads from its own local
                    audit_log table with no audit-service at all. */}
                <Route path={ROUTES.CMS_AUDIT}     element={<Navigate to={ROUTES.ADMIN_AUDIT_LOGS} replace />} />
                <Route path={ROUTES.CMS_SETTINGS}  element={<CmsSettingsPage />} />
                <Route path={ROUTES.CMS_METRICS}    element={<CmsMetricsPage />} />
                <Route path={ROUTES.CMS_CERT_COLLABORATION} element={<CmsCertCollaborationGuard />} />
                <Route path={ROUTES.CMS_CUSTOMERS} element={<CmsCustomersGuard />} />
              </Route>

              {/* TBA placeholder */}
              <Route path={ROUTES.TBA} element={<Navigate to={ROUTES.HOME} replace />} />
            </Route>
          </Route>

          {/* Fallback — "Page not found" inside the app for signed-in users,
              the public landing for everyone else (see NotFoundPage). */}
          <Route path="*" element={<NotFoundPage />} />
        </SentryRoutes>
      </Suspense>
      <Toaster position="top-right" />
      <ThemePlugin />
    </BrowserRouter>
  );
}

